/**
 * Standard Deviation (StdDev)
 *
 * Measures price volatility as the standard deviation of the source
 * over `period` bars. Higher values indicate higher volatility.
 *
 * Category: Volatility | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { getSource, computeSMA } from "./sma";

export const stddevIndicator: IndicatorDefinition = {
  id: "stddev",
  name: "Standard Deviation",
  shortName: "STDDEV",
  category: "volatility",
  pane: "separate",
  params: [
    { key: "period", label: "Period", type: "number", default: 20, min: 2, max: 500 },
    { key: "source", label: "Source", type: "select", default: "close", options: [
      { label: "Close", value: "close" }, { label: "Open", value: "open" },
      { label: "High", value: "high" }, { label: "Low", value: "low" },
    ]},
    { key: "color", label: "Color", type: "color", default: "#9C27B0" },
  ],
  plots: [
    { key: "stddev", label: "StdDev", color: "#9C27B0", style: "line", lineWidth: 2, priceScaleId: "stddev" },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, source = "close" } = params;
    const src = getSource(candles, source);
    const sma = computeSMA(src, period);
    const data: { time: number; value: number }[] = [];

    for (let i = period - 1; i < src.length; i++) {
      const mean = sma[i];
      if (isNaN(mean)) continue;
      let sqSum = 0;
      for (let j = i - period + 1; j <= i; j++) {
        sqSum += (src[j] - mean) ** 2;
      }
      data.push({ time: candles[i].time, value: Math.sqrt(sqSum / period) });
    }

    return [{ key: "stddev", data }];
  },
};
